/**
 * useAnnotationClusters — Grid-basiertes Screen-Space-Clustering
 *
 * Bei großer Distanz (Übersicht-Stufe) werden nahe Annotationen
 * zu Clustern zusammengefasst — analog zu Google Maps Marker-Clustering.
 *
 * Algorithmus:
 * 1. Projiziere alle Annotationen in Screen-Space
 * 2. Grid-basiertes Clustering (Zellgröße: 60px)
 * 3. Cluster-Marker zeigt Anzahl + dominanten Typ (Farbe)
 */

import { useMemo, useRef, useEffect, useState } from 'react'
import { Annotation, ANNOTATION_TYPE_COLORS } from '../types/annotations'
import type { ZoomTier } from './useSemanticZoom'
import type { AnnotationCluster } from '../components/annotations/ClusterMarker'

const GRID_SIZE = 60        // px — Zellgröße im Screen-Space
const MIN_CLUSTER_SIZE = 2  // Mindestanzahl für Cluster-Bildung

interface UseAnnotationClustersOptions {
  annotations: Annotation[]
  viewerRef: React.RefObject<any>
  tier: ZoomTier
  enabled: boolean
}

interface ClusterResult {
  clusters: AnnotationCluster[]
  /** IDs von Annotationen die in einem Cluster sind (nicht einzeln rendern) */
  clusteredIds: Set<string>
}

export function useAnnotationClusters({
  annotations,
  viewerRef,
  tier,
  enabled,
}: UseAnnotationClustersOptions): ClusterResult {
  const [clusters, setClusters] = useState<AnnotationCluster[]>([])
  const [clusteredIds, setClusteredIds] = useState<Set<string>>(new Set())
  const annotationsRef = useRef(annotations)
  annotationsRef.current = annotations

  // Cluster-Berechnung: periodisch (alle 300ms) im Overview-Modus
  useEffect(() => {
    if (!enabled || tier !== 'overview') {
      setClusters([])
      setClusteredIds(new Set())
      return
    }

    function computeClusters() {
      const viewer = viewerRef.current
      const THREE = (window as any).Potree?.THREE
      if (!viewer || !THREE) return
      if (!viewer.renderer?.domElement || !viewer.scene?.getActiveCamera) return

      const camera = viewer.scene.getActiveCamera()
      if (!camera) return

      camera.updateMatrixWorld(true)
      const canvas = viewer.renderer.domElement
      const W = canvas.clientWidth
      const H = canvas.clientHeight

      const anns = annotationsRef.current

      // 1. Alle sichtbaren Annotationen in Screen-Space projizieren
      const projected: Array<{ annotation: Annotation; sx: number; sy: number }> = []
      for (const ann of anns) {
        const vec = new THREE.Vector3(ann.position.x, ann.position.y, ann.position.z)
        vec.project(camera)
        if (vec.z > 1) continue // hinter Kamera
        const sx = ((vec.x + 1) / 2) * W
        const sy = ((-vec.y + 1) / 2) * H
        if (sx < -50 || sx > W + 50 || sy < -50 || sy > H + 50) continue
        projected.push({ annotation: ann, sx, sy })
      }

      // 2. Grid-basiertes Clustering
      const grid: Map<string, typeof projected> = new Map()
      for (const item of projected) {
        const gx = Math.floor(item.sx / GRID_SIZE)
        const gy = Math.floor(item.sy / GRID_SIZE)
        const key = `${gx}:${gy}`
        if (!grid.has(key)) grid.set(key, [])
        grid.get(key)!.push(item)
      }

      // 3. Cluster bilden (Zellen mit >= MIN_CLUSTER_SIZE)
      const newClusters: AnnotationCluster[] = []
      const newClusteredIds = new Set<string>()

      // Nachbarzellen zusammenführen für bessere Cluster
      const visited = new Set<string>()
      for (const [key, items] of grid) {
        if (visited.has(key)) continue
        if (items.length < MIN_CLUSTER_SIZE) continue

        // Einfache 1-Zellen-Cluster (keine Nachbar-Merge für Performance)
        visited.add(key)

        const clusterAnns = items.map(i => i.annotation)
        const centerX = items.reduce((s, i) => s + i.sx, 0) / items.length
        const centerY = items.reduce((s, i) => s + i.sy, 0) / items.length

        // Dominanter Typ bestimmen
        const typeCounts: Record<string, number> = {}
        for (const ann of clusterAnns) {
          typeCounts[ann.type] = (typeCounts[ann.type] || 0) + 1
        }
        const dominantType = Object.entries(typeCounts)
          .sort((a, b) => b[1] - a[1])[0]![0]

        newClusters.push({
          id: `cluster-${key}`,
          annotations: clusterAnns,
          centerScreen: { x: centerX, y: centerY },
          dominantType,
          dominantColor: ANNOTATION_TYPE_COLORS[dominantType as keyof typeof ANNOTATION_TYPE_COLORS] ?? '#999',
        })

        for (const ann of clusterAnns) {
          newClusteredIds.add(ann.id)
        }
      }

      setClusters(newClusters)
      setClusteredIds(newClusteredIds)
    }

    computeClusters()
    const interval = setInterval(computeClusters, 300)
    return () => clearInterval(interval)
  }, [enabled, tier, viewerRef])

  return { clusters, clusteredIds }
}
